import { tt } from '@agos/shared'

/**
 * The order types a customer may choose — `FRONTEND.md` §9.50.
 *
 * The checkout hard-coded `['delivery', 'bar']` with two labels and two icons, so a franchise that
 * created a channel and marked it *available on web* saw nothing change. This reads the channels the
 * server says are offered, and every branch below asks what a channel **does** rather than what it is
 * called — §9.5's rule, arriving on the customer site.
 */
export interface OrderTypeOption {
  slug: string
  name: string
  requiresDeliveryAddress: boolean
  usesTables: boolean
  onPremise: boolean
}

/**
 * Legacy's pair, kept as the answer when the server offers nothing.
 *
 * Not a default anybody chose — a franchise with no web-available channel, or a request that failed,
 * still needs a usable checkout. An empty picker is a checkout nobody can complete, which is worse
 * than the two types every restaurant here has had since before this table existed.
 */
export const FALLBACK_ORDER_TYPES: OrderTypeOption[] = [
  {
    slug: 'delivery',
    name: tt('Rozvoz'),
    requiresDeliveryAddress: true,
    usesTables: false,
    onPremise: false,
  },
  {
    slug: 'bar',
    name: tt('Osobný odber'),
    requiresDeliveryAddress: false,
    usesTables: false,
    onPremise: true,
  },
]

/** `GET /sales-channels/public`'s rows, in the order the franchise put them in. */
export function orderTypesOf(payload: unknown): OrderTypeOption[] {
  if (!Array.isArray(payload)) return FALLBACK_ORDER_TYPES
  const offered = payload.flatMap((entry) => {
    const row = entry as Record<string, unknown>
    const slug = typeof row.slug === 'string' && row.slug !== '' ? row.slug : null
    if (slug === null) return []
    return [
      {
        slug,
        name: typeof row.name === 'string' && row.name !== '' ? row.name : slug,
        requiresDeliveryAddress: row.requiresDeliveryAddress === true,
        usesTables: row.usesTables === true,
        onPremise: row.onPremise === true,
        sortOrder: typeof row.sortOrder === 'number' ? row.sortOrder : 0,
      },
    ]
  })
  if (offered.length === 0) return FALLBACK_ORDER_TYPES
  return offered
    .sort((left, right) => left.sortOrder - right.sortOrder || left.slug.localeCompare(right.slug))
    .map(({ sortOrder: _sortOrder, ...option }) => option)
}

/** The chosen channel, or the first offered — never `undefined`, so no branch has to guess. */
export function chosenType(types: readonly OrderTypeOption[], slug: string): OrderTypeOption {
  return types.find((option) => option.slug === slug) ?? types[0] ?? FALLBACK_ORDER_TYPES[0]!
}
